import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useState } from "react";
import { config } from "../config";

function Checkout({
  billingFirstName,
  setBillingFirstName,
  billingLastName,
  setBillingLastName,
  billingEmail,
  setBillingEmail,
  billingAddress,
  setBillingAddress,
  billingPhone,
  setBillingPhone,
  billingCity,
  setBillingCity,
  billingState,
  setBillingState,
  billingZip,
  setBillingZip,
  cart,
  theme,
  closeModal,
  onBack,
  total,
  discount,
  discountCodeId,
  discountCode,
  deliveryFee,
  tax,
  setTax,
  productTotal,
  contactFirstName,
  contactLastName,
  contactBusinessName,
  contactEmail,
  contactPhone,
  shippingMethod,
  deliveryOption,
  deliveryDate,
  deliveryTime,
  deliveryAddress,
  setShippingMethod,
  setDeliveryOption,
  setDeliveryDate,
  setDeliveryTime,
  setDeliveryAddress,
  deliveryCity,
  setDeliveryCity,
  deliveryState,
  setDeliveryState,
  deliveryZip,
  setDeliveryZip,
  pickupDate,
  setPickupDate,
  pickupTime,
  setPickupTime,
  setTotalDriveTime,
  setContactFirstName,
  setContactLastName,
  setContactBusinessName,
  setContactEmail,
  setContactPhone,
  setCart,
  store,
  onSuccess,
  confirmationCode,
  setConfirmationCode,
  orderId,
  setOrderId,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const spinnerStyles = {
    spinner: {
      border: "2px solid #ccc",
      borderTop: `2px solid ${theme.primaryFontColor}`,
      borderRadius: "50%",
      width: "12px",
      height: "12px",
      animation: "spin 1s linear infinite",
      justifySelf: "center",
    },
  };

  const handleReset = () => {
    setBillingFirstName("");
    setBillingLastName("");
    setBillingEmail("");
    setBillingAddress("");
    setBillingPhone("");
    setBillingCity("");
    setBillingState("");
    setBillingZip("");
    setTax(0.0);
    setContactFirstName("");
    setContactLastName("");
    setContactBusinessName("");
    setContactEmail("");
    setContactPhone("");
    setShippingMethod("deliver");
    setDeliveryOption("");
    setDeliveryDate("");
    setDeliveryTime("");
    setDeliveryAddress("");
    setDeliveryCity("");
    setDeliveryState("");
    setDeliveryZip("");
    setPickupDate("");
    setPickupTime("");
    setTotalDriveTime(0);
    setCart([]);
    setOrderId("");
    setIsProcessing(false);
  };

  const handleSubmit = async (event) => {
    console.log("attempting to submit order...");
    event.preventDefault();

    let newOrder;

    if (isProcessing || !stripe || !elements) {
      console.log("Processing already in progress or Stripe not loaded.");
      alert(
        isProcessing ? "Processing in progress." : "Stripe has not loaded."
      );
      return;
    }

    setIsProcessing(true);

    if (
      !billingFirstName.trim() ||
      !billingLastName.trim() ||
      !billingEmail.trim() ||
      !billingPhone.trim() ||
      !billingAddress.trim() ||
      !billingCity.trim() ||
      !billingState.trim() ||
      !billingZip.trim()
    ) {
      alert("Please fill in all billing details.");
      setIsProcessing(false);
      return;
    }

    const createOrder = async () => {
      console.log("Creating order...");
      const response = await fetch(
        `${config.apiUrl}/ecomm-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(order_data),
        }
      );
      const result = await response.json();
      console.log("Order creation result:", result);
      
      if (!response.ok || result.error) {
        throw new Error(result.error || 'Failed to create order');
      }
      
      return result;
    };

    const logChargeAttempt = async (
      status,
      transactionId,
      orderId,
      confirmationCode,
      errorMessage
    ) => {
      console.log("Logging charge attempt:", {
        confirmationCode,
        orderId,
        status,
        transactionId,
        errorMessage,
      });
      await fetch(`${config.apiUrl}/log-charge-attempt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          confirmation_code: confirmationCode,
          order_id: orderId,
          status,
          amount: total.toFixed(2),
          stripe_transaction_id: transactionId,
          error_message: errorMessage,
          order_data: order_data,
        }),
      });
    };

    const checkOrderExists = async () => {
      console.log(
        "Checking if order exists with confirmationCode:",
        confirmationCode
      );
      const response = await fetch(
        `${config.apiUrl}/check-ecomm-order-exists`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ confirmation_code: confirmationCode }),
        }
      );
      const result = await response.json();
      console.log("Order existence check result:", result);
      return result;
    };

    const order_data = {
      billing_firstname: billingFirstName,
      billing_lastname: billingLastName,
      billing_email: billingEmail,
      billing_phone: billingPhone,
      billing_address: billingAddress,
      billing_city: billingCity,
      billing_state: billingState,
      billing_zip: billingZip,
      contact_firstname: contactFirstName,
      contact_lastname: contactLastName,
      contact_businessname: contactBusinessName,
      contact_email: contactEmail,
      contact_phone: contactPhone,
      shipping_method: shippingMethod,
      delivery_option: deliveryOption,
      delivery_date: deliveryDate,
      delivery_time: deliveryTime,
      delivery_address: deliveryAddress,
      delivery_city: deliveryCity,
      delivery_state: deliveryState,
      delivery_zip: deliveryZip,
      pickup_date: pickupDate,
      pickup_time: pickupTime,
      cart: cart,
      product_total: productTotal.toFixed(2),
      discount: discount.toFixed(2),
      tax: tax.toFixed(2),
      delivery_fee: deliveryFee.toFixed(2),
      total_charge: total.toFixed(2),
      discount_code_id: discountCodeId,
      discount_code: discountCode,
      store_id: store.store_id,
      store_email: store.contact_email,
      store: store,
    };

    try {
      if (confirmationCode) {
        console.log("Confirmation code exists, checking for order...");
        const existingOrder = await checkOrderExists();
        if (JSON.stringify(existingOrder.data) !== JSON.stringify(order_data)) {
          console.log("Order data mismatch. Creating a new order...");
          newOrder = await createOrder();
          setConfirmationCode(newOrder.confirmation_code);
          setOrderId(newOrder.order_id);
        } else {
          console.log("Order already exists. Skipping creation.");
        }
      } else {
        console.log("No confirmation code found. Creating a new order...");
        newOrder = await createOrder();
        setConfirmationCode(newOrder.confirmation_code);
        setOrderId(newOrder.order_id);
      }
    } catch (error) {
      console.error("Error during order creation or checking:", error);
      alert("Failed to create or check order. Please try again.");
      setIsProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    console.log("Creating payment method...");
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name: `${billingFirstName} ${billingLastName}`,
        email: billingEmail,
        address: {
          line1: billingAddress,
          city: billingCity,
          state: billingState,
          postal_code: billingZip,
        },
        phone: billingPhone,
      },
    });

    if (error) {
      console.error("Payment method creation error:", error.message);
      alert(`Payment Method Error: ${error.message}`); // Errors seem descriptive (e.g. "Your card's security code is incomplete.")
      setIsProcessing(false);
      return;
    }

    console.log("Payment method created:", paymentMethod);

    try {
      console.log("Creating payment intent...");
      const paymentResponse = await fetch(
        `${config.apiUrl}/create-payment-intent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: Math.round(total * 100),
            paymentMethodId: paymentMethod.id,
          }),
        }
      );
      const { paymentIntent } = await paymentResponse.json();
      console.log("Payment intent response:", paymentIntent);

      if (paymentIntent.status !== "succeeded") {
        console.log("Confirming card payment...");
        const paymentResult = await stripe.confirmCardPayment(
          paymentIntent.client_secret
        );
        console.log("Card payment result:", paymentResult);

        // console.log("ORDER INFORMATION:::::::::", newOrder);

        if (paymentResult.error) {
          console.error("Card payment error:", paymentResult.error.message);
          await logChargeAttempt(
            "failed",
            paymentResult.paymentIntent?.id,
            newOrder.order_id,
            newOrder.confirmation_code,
            paymentResult.error.message
          );
          alert(`Payment failed: ${paymentResult.error.message}`);
          setIsProcessing(false);
          return;
        }

        if (paymentResult.paymentIntent.status === "succeeded") {
          console.log(
            "Card payment succeeded:",
            paymentResult.paymentIntent.id
          );
          await logChargeAttempt(
            "succeeded",
            paymentResult.paymentIntent.id,
            newOrder.order_id,
            newOrder.confirmation_code,
            null
          );
          setConfirmationCode(null);
          setIsProcessing(false);
          handleReset();
          onSuccess();
        }
      }
      // console.log("ORDER INFORMATION:::::::::", newOrder);
      console.log("order was successfully created on the first try.");
      await logChargeAttempt(
        "succeeded",
        paymentIntent.id,
        newOrder.order_id,
        newOrder.confirmation_code,
        null
      );
      setIsProcessing(false);
      handleReset();
      onSuccess();
    } catch (error) {
      console.error("Error during payment:", error);
      alert("Payment failed. Please try again.");
      setIsProcessing(false);
    }
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      // width: "100%",
      // minHeight: "100vh",
      // position: "relative",
      // overflow: "auto",
    },
    title: {
      color: theme.primaryFontColor,
      fontSize: "20px",
      marginBottom: "20px",
    },
    inputRow: {
      display: "flex",
      gap: "10px",
    },
    input: {
      display: "block",
      width: "100%",
      marginBottom: "10px",
      padding: "10px",
      border: "1px solid #ccc",
      borderRadius: "5px",
    },
    footer: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      padding: "10px 20px",
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: "white",
      borderTop: "1px solid #ccc",
      borderBottomLeftRadius: "10px",
      borderBottomRightRadius: "10px",
    },
    button: {
      backgroundColor: theme.accentColor,
      color: "white",
      border: "none",
      padding: "10px 15px",
      borderRadius: "5px",
      cursor: "pointer",
      width: 150,
    },
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Checkout</h3>
      <div
        name="divider"
        style={{
          backgroundColor: theme.primaryFontColor,
          height: 1,
          marginBottom: 20,
        }}
      />
      <div
        name="scrollable-content"
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1, // Allow the scrollable content to take the remaining height
          overflowY: "auto", // Enable vertical scrolling
          padding: "10px",
          paddingBottom: 125,
          // backgroundColor: "red",
        }}
      >
        <form onSubmit={handleSubmit} style={{}}>
          <div
            style={{
              border: "1px solid #ccc",
              borderRadius: "5px",
              padding: "10px",
              marginBottom: "10px",
              // backgroundColor: "red",
            }}
          >
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "14px",
                    color: "#424770",
                    lineHeight: "24px",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
            />
          </div>

          <h4 style={styles.title}>Billing Details</h4>

          <div style={styles.inputRow}>
            <input
              style={styles.input}
              type="text"
              placeholder="First Name"
              value={billingFirstName}
              onChange={(e) => setBillingFirstName(e.target.value)}
            />
            <input
              style={styles.input}
              type="text"
              placeholder="Last Name"
              value={billingLastName}
              onChange={(e) => setBillingLastName(e.target.value)}
            />
          </div>
          <div style={styles.inputRow}>
            <input
              style={styles.input}
              type="email"
              placeholder="Email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
            />
            <input
              style={styles.input}
              type="tel"
              placeholder="Phone Number"
              value={billingPhone}
              onChange={(e) => setBillingPhone(e.target.value)}
            />
          </div>
          <div style={styles.inputRow}>
            <input
              style={{ ...styles.input, marginTop: 20 }}
              type="text"
              placeholder="Billing Address"
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
            />
          </div>
          <div style={styles.inputRow}>
            <input
              style={styles.input}
              type="text"
              placeholder="City"
              value={billingCity}
              onChange={(e) => setBillingCity(e.target.value)}
            />
            <input
              style={styles.input}
              type="text"
              placeholder="State"
              value={billingState}
              onChange={(e) => setBillingState(e.target.value)}
            />
            <input
              style={styles.input}
              type="text"
              placeholder="Zip Code"
              value={billingZip}
              onChange={(e) => setBillingZip(e.target.value)}
            />
          </div>
        </form>
      </div>

      <div style={styles.footer}>
        <div style={{ flex: 2, textAlign: "right" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              justifySelf: "flex-end",
            }}
          >
            <div
              style={{
                width: "35%",
              }}
            />
            <h4
              style={{
                color: "black",
                marginBottom: "10px",
                fontWeight: "400",
              }}
            >
              Order Total:
            </h4>
            <h4
              style={{
                color: theme.primaryFontColor,
                marginBottom: "10px",
                textAlign: "right",
              }}
            >
              ${total.toFixed(2)}
            </h4>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <button style={styles.button} onClick={onBack}>
              Back
            </button>
            <button
              style={{
                ...styles.button,
                opacity: isProcessing ? 0.6 : 1,
                justifyContent: "center",
              }}
              type="button" // Changed to "button" to prevent default form submission
              disabled={isProcessing}
              onClick={handleSubmit} // Added the onClick handler
            >
              {isProcessing ? (
                <div style={spinnerStyles.spinner}></div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
